import TextField, { TextFieldVariants } from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConsultantList from '@src/cache/ConsultantList';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useAuth } from '@src/auth/hooks/useAuth';
import ConsultantNewDTO from '@src/adapters/dto/consultant/ConsultantNewDTO';
import useCreateConsultantLazy from '@src/adapters/hooks/consultants/useCreateConsultant';
import useCachedSpecialitiesList from '@src/adapters/hooks/specialities/useCachedSpecialitiesList';

interface Props {
  onSelection?(consultantId: string | null): void;
  selectedId: string | null;
  enableNew?: boolean;
  variant?: TextFieldVariants;
  disabled?: boolean;
}

function ConsultantAutoComplete({
  onSelection,
  selectedId,
  enableNew = false,
  variant,
  disabled,
}: Props) {
  const { execute: createConsultant } = useCreateConsultantLazy();
  const { getList: getConsultants } = ConsultantList();
  const { getList: getSpecialities } = useCachedSpecialitiesList();
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<IdNameDto[]>([]);
  const [selected, setSelected] = useState<IdNameDto | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const { currentHospitalId } = useAuth();
  const parent = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ConsultantNewDTO>();

  useEffect(() => {}, [getConsultants, selectedId]);

  const findConsultants = useCallback(async () => {
    const result = await getConsultants(selectedId ? selectedId : '');

    const orderedList = result.sort((a, b) =>
      a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase())
    );

    const availableConsultants: IdNameDto[] = [];

    for (const item of orderedList) {
      let specialityName = '';

      if (item.specialityId) {
        const specialities = await getSpecialities(item.specialityId);
        if (specialities && specialities.length > 0) {
          specialityName = specialities[0].name;
        }
      }

      let label = [
        item.title,
        item.lastName,
        item.firstName ? item.firstName.substring(0, 1) : '',
        specialityName ? `(${specialityName})` : '',
      ]
        .filter(Boolean)
        .join(' ');

      availableConsultants.push({
        id: item.id ? item.id : '',
        name: label,
      });
    }

    setAvailable(availableConsultants);
  }, [getConsultants, getSpecialities, selectedId]);

  useEffect(() => {
    findConsultants();
  }, [findConsultants]);

  useEffect(() => {
    if (selectedId) {
      const consultant = available.find((a) => a.id === selectedId);
      if (consultant) {
        setSelected(consultant || null);
        setInputValue(consultant ? consultant.name : '');
      }
    } else {
      setSelected(null);
      setInputValue('');
    }
  }, [available, selectedId]);

  const onOpen = () => {
    setOpen(true);
  };

  const updateSelected = (value: IdNameDto | null) => {
    setSelected(value);
    setInputValue(value ? value.name : '');
    if (onSelection) {
      onSelection(value?.id || null);
    }
  };

  const handleAddNewItem = () => {
    setValue('lastName', newValue);
    setAddOpen(true);
  };

  const handleCancelAdd = () => {
    reset();
    setAddOpen(false);
  };

  const onSubmit = async (data: ConsultantNewDTO) => {
    if (currentHospitalId) {
      data.hospitals = [currentHospitalId];
    }
    const result = await createConsultant({
      consultant: data,
    });

    if (result.consultant) {
      reset();
      setAddOpen(false);
      const savedConsultant: IdNameDto = {
        id: result.consultant.id!,
        name: result.consultant.lastName,
      };
      if (onSelection) {
        onSelection(savedConsultant.id);
      }
    }
  };

  return (
    <>
      <Autocomplete
        fullWidth
        freeSolo={enableNew}
        selectOnFocus
        handleHomeEndKeys
        ref={parent}
        open={open}
        value={selected}
        inputValue={inputValue}
        disabled={disabled}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, value) => {
          if (value) {
            if ((value as IdNameDto).id) {
              updateSelected(value as IdNameDto);
            } else {
              handleAddNewItem();
            }
          } else {
            updateSelected(null);
          }
        }}
        onOpen={onOpen}
        onClose={() => {
          setOpen(false);
        }}
        filterOptions={(options, params) => {
          if (!enableNew) {
            return options.filter((o) =>
              o.name.toLowerCase().includes(params.inputValue.toLowerCase())
            );
          }
          const { inputValue } = params;
          const isExisting = options.some(
            (option) => inputValue === option.name
          );
          if (inputValue !== '' && !isExisting) {
            setNewValue(inputValue);
            options.push({
              id: '',
              name: `Add "${inputValue}"`,
            });
          }
          return options.filter((o) =>
            o.name.toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.name
        }
        options={[...available]}
        filterSelectedOptions={true}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Consultant"
            variant={variant}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      <Popover
        open={addOpen}
        aria-labelledby="Consultant Details"
        aria-describedby="Consultant Details"
        disableEnforceFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={parent ? parent?.current : null}>
        <Box sx={{ padding: '20px', width: '450px', height: '600px' }}>
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}>
            New Consultant
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                  />
                )}
              />
              {errors.title && <p>{errors.title.message}</p>}
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                  />
                )}
              />
              {errors.firstName && <p>{errors.firstName.message}</p>}
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                  />
                )}
              />
              {errors.lastName && <p>{errors.lastName.message}</p>}
              <Controller
                name="gmcReference"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="GMC Reference"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                  />
                )}
              />
              {errors.gmcReference && <p>{errors.gmcReference.message}</p>}
            </DialogContent>
            <DialogActions sx={{ flex: '1', marginLeft: '8px' }}>
              <Button
                onClick={handleCancelAdd}
                color="primary"
                variant="outlined"
                sx={{ flex: '1', marginRight: '8px' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ flex: '1', marginRight: '8px' }}>
                Add
              </Button>
            </DialogActions>
          </form>
        </Box>
      </Popover>
    </>
  );
}
export default ConsultantAutoComplete;
